<script setup lang="ts">
import { useStore } from '@/stores/store'
import { toTypedSchema } from '@vee-validate/zod'
import { storeToRefs } from 'pinia'
import { Field } from 'vee-validate'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { z } from 'zod'
import BigSizePopup from '@/components/SizeAndButtons/BigSizePopup.vue'
import { IconInfo } from '@/components/icons'

const { t } = useI18n()
const store = useStore()
const { length_b_rectangle, width_a_rectangle } = storeToRefs(store)
const RECTANGLE_MIN_VALUE = 30
const RECTANGLE_MAX_VALUE = 240
const RECTANGLE_BIG_SIZE = 160

const widthFieldRule = toTypedSchema(
  z.coerce
    .number({
      required_error: `${t('Width')} ${t('must be filled in.')}`,
      invalid_type_error: `${t('Width')} ${t('must be a number.')}`,
    })
    .min(RECTANGLE_MIN_VALUE, {
      message: `${t('Width')} ${t('must be greater than or equal to')} ${RECTANGLE_MIN_VALUE}`,
    })
    .max(RECTANGLE_MAX_VALUE, {
      message: `${t('Width')} ${t('must be less than or equal to')} ${RECTANGLE_MAX_VALUE}`,
    }),
)
const lengthFieldRule = toTypedSchema(
  z.coerce
    .number({
      required_error: `${t('Length')} ${t('must be filled in.')}`,
      invalid_type_error: `${t('Length')} ${t('must be a number.')}`,
    })
    .min(RECTANGLE_MIN_VALUE, {
      message: `${t('Length')} ${t('must be greater than or equal to')} ${RECTANGLE_MIN_VALUE}`,
    })
    .max(RECTANGLE_MAX_VALUE, {
      message: `${t('Length')} ${t('must be less than or equal to')} ${RECTANGLE_MAX_VALUE}`,
    }),
)


const isBigSize = computed(
  () =>
    Number(length_b_rectangle.value) >= RECTANGLE_BIG_SIZE ||
    Number(width_a_rectangle.value) >= RECTANGLE_BIG_SIZE,
)
</script>
<template>
  <div class="fields-wrapper space-y-6 lg:space-y-10">
    <div class="form-group">
      <label class="items-center" for="length_b_rectangle">
        <span class="field-label">{{ $t('Length') }}</span>
        <span class="field-condition">{{ $t('Minimum') + ' ' + RECTANGLE_MIN_VALUE }}</span>
      </label>
      <div class="relative">
      <Field
        validateOnInput
        type="number"
        name="length_b_rectangle"
        v-model="length_b_rectangle"
        :rules="lengthFieldRule"
        v-slot="{ field, errorMessage }"
      >

        <input
          v-bind="field"
          id="length_b_rectangle"
          class="!field-input appearance-textfield"
          :placeholder="$t('Enter the value')"
          name="length_b_rectangle"
          type="number"
          :min="RECTANGLE_MIN_VALUE"
          :max="RECTANGLE_MAX_VALUE"
          inputmode="numeric"
          :class="{ 'has-error': errorMessage }"
        />

        <span v-if="errorMessage">{{ errorMessage }}</span>
      </Field>
      <span class="absolute right-6 top-1/3 text-black/40">cm</span>
      </div>
    </div>
    <div class="form-group">
      <label class="items-center" for="width_a_rectangle">
        <span class="field-label">{{ $t('Width') }}</span>
        <span class="field-condition">{{ $t('Minimum') + ' ' + RECTANGLE_MIN_VALUE }}</span>
      </label>
      <div class="relative">
      <Field
        keepValue
        validateOnInput
        name="width_a_rectangle"
        type="number"
        v-model="width_a_rectangle"
        :rules="widthFieldRule"
        v-slot="{ field, errorMessage }"
      >
        <input
          v-bind="field"
          class="!field-input appearance-textfield"
          id="width_a_rectangle"
          :placeholder="$t('Enter the value')"
          type="number"
          :min="RECTANGLE_MIN_VALUE"
          :max="RECTANGLE_MAX_VALUE"
          inputmode="numeric"
          :class="{ 'has-error': errorMessage }"
        />
        <span v-if="errorMessage">{{ errorMessage }}</span>
      </Field>
      <span class="absolute right-6 top-1/3 text-black/40">cm</span>
    </div>
    </div>
    <div
      v-if="isBigSize"
      class="contact-form-link flex flex-col items-start bg-black/80 px-6 py-4 text-white w-full max-w-[480px] space-y-4"
    >
      <div class="flex gap-2 justify-center items-center">
        <IconInfo class="text-white w-6 h-6" />
        <span class="w-full">
          {{
            $t(
              "To make a cushion of the measurements you've entered, we need to assemble two or more pieces of material.",
            )
          }}
        </span>
      </div>
      <BigSizePopup v-if="isBigSize" />
    </div>

  </div>
</template>

<style scoped>
.full-row {
  width: 100%;
}

.mb-3 {
  margin-bottom: 2rem;
}


</style>
